import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowsClockwise, CircleNotch, FileXls, FilePdf, WarningCircle, TrendUp, Factory, Package,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { api, registerError } from "@/components/consumption/registerApi";

const ALL = "__all__";

const money = (value) =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const num = (value, digits = 3) =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: digits });

const VALUE_NOTE = {
  sap: { label: "SAP", className: "text-[#027A48]" },
  awaiting_sap: { label: "awaiting SAP value", className: "text-[#B54708]" },
  not_posted: { label: "not posted", className: "text-[#B42318]" },
};

const Kpi = ({ label, value, sub, testId, tone = "teal" }) => (
  <div className="bg-white border border-[#E4E7EC] rounded-xl px-4 py-3" data-testid={testId}>
    <p className="text-[11px] font-bold uppercase tracking-wide text-[#667085]">{label}</p>
    <p className={`font-heading text-lg font-bold mt-0.5 ${tone === "teal" ? "text-[#0E7C86]" : "text-[#101828]"}`}>
      {value}
    </p>
    {sub && <p className="text-[11px] text-[#667085] mt-0.5">{sub}</p>}
  </div>
);

const DayBars = ({ daily }) => {
  const peak = Math.max(1, ...daily.map((d) => Math.abs(d.value || 0)), ...daily.map((d) => Math.abs(d.quantity || 0)));
  if (!daily.length) return <p className="text-sm text-[#667085]" data-testid="audit-daily-empty">No consumption in this period.</p>;
  return (
    <div className="flex items-end gap-1.5 overflow-x-auto pb-6 pt-2" data-testid="audit-daily-chart">
      {daily.map((day) => {
        const scale = Math.abs(day.value || 0) || Math.abs(day.quantity || 0);
        return (
          <div key={day.date} className="flex-1 min-w-[30px] max-w-[72px] flex flex-col items-center justify-end gap-1 group relative">
            <div
              className={`w-5 rounded-t-sm transition-all duration-200 ${day.flagged ? "bg-[#F79009]" : "bg-[#0E7C86]"} group-hover:opacity-80`}
              style={{ height: `${Math.max(6, Math.round((scale / peak) * 104))}px` }}
              data-testid={`audit-daily-bar-${day.date}`}
            />
            {!!day.returned_value && (
              <div
                className="w-5 bg-[#B42318]/70 rounded-b-sm"
                style={{ height: `${Math.max(3, Math.round((Math.abs(day.returned_value) / peak) * 104))}px` }}
                title={`₹${money(day.returned_value)} returned to store`}
                data-testid={`audit-daily-return-${day.date}`}
              />
            )}
            <span className="text-[9px] text-[#667085] whitespace-nowrap">{day.date.slice(5)}</span>
            <div className="hidden group-hover:block absolute bottom-full mb-1 z-10 bg-[#101828] text-white text-[10px] rounded px-2 py-1 whitespace-nowrap">
              {day.date} · ₹{money(day.value)} consumed · {num(day.quantity)} qty
              {day.returned_value ? ` · ₹${money(day.returned_value)} returned` : ""}
              {day.flagged ? ` · ${day.flagged} flagged` : ""}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const ConsumptionAuditDashboard = () => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const monthAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return d.toISOString().slice(0, 10);
  }, []);

  const [options, setOptions] = useState({ sites: [], departments: [], items: [] });
  const [filters, setFilters] = useState({ date_from: monthAgo, date_to: today, site_id: "", department_id: "", product_id: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("ledger");

  useEffect(() => {
    api.auditOptions().then((o) => {
      setOptions(o);
      // The plant's day is IST, the browser's may not be - between midnight and
      // 05:30 the two disagree, and the register's day is the one that counts.
      if (o.today && o.today !== today) {
        const from = new Date(`${o.today}T00:00:00Z`);
        from.setUTCDate(from.getUTCDate() - 29);
        setFilters((f) => ({ ...f, date_from: from.toISOString().slice(0, 10), date_to: o.today }));
      }
    }).catch(() => {});
  }, [today]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.auditDashboard({ ...filters, row_limit: 500 }));
    } catch (e) {
      toast.error(registerError(e).message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const departments = useMemo(
    () => options.departments.filter((d) => !filters.site_id || d.site_id === filters.site_id),
    [options.departments, filters.site_id],
  );

  const summary = data?.summary;
  const periods = data?.periods;

  return (
    <div className="space-y-4" data-testid="consumption-audit">
      <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="audit-filters">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-[11px] font-bold text-[#344054]">From</Label>
            <Input
              type="date" className="h-9 mt-1" value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              data-testid="audit-filter-from"
            />
          </div>
          <div>
            <Label className="text-[11px] font-bold text-[#344054]">To</Label>
            <Input
              type="date" className="h-9 mt-1" value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              data-testid="audit-filter-to"
            />
          </div>
          <div>
            <Label className="text-[11px] font-bold text-[#344054]">Site</Label>
            <Select
              value={filters.site_id || ALL}
              onValueChange={(v) => setFilters((f) => ({ ...f, site_id: v === ALL ? "" : v, department_id: "" }))}
            >
              <SelectTrigger className="h-9 mt-1" data-testid="audit-filter-site"><SelectValue placeholder="All sites" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="audit-filter-site-all">All sites</SelectItem>
                {options.sites.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`audit-filter-site-${s}`}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-bold text-[#344054]">Department</Label>
            <Select
              value={filters.department_id || ALL}
              onValueChange={(v) => setFilters((f) => ({ ...f, department_id: v === ALL ? "" : v }))}
            >
              <SelectTrigger className="h-9 mt-1" data-testid="audit-filter-department"><SelectValue placeholder="All departments" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="audit-filter-department-all">All departments</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`audit-filter-department-${d.id}`}>
                    {d.name} · {d.site_id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] font-bold text-[#344054]">Item</Label>
            <Select
              value={filters.product_id || ALL}
              onValueChange={(v) => setFilters((f) => ({ ...f, product_id: v === ALL ? "" : v }))}
            >
              <SelectTrigger className="h-9 mt-1" data-testid="audit-filter-item"><SelectValue placeholder="All items" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ALL} data-testid="audit-filter-item-all">All items</SelectItem>
                {options.items.map((i) => (
                  <SelectItem key={i.product_id} value={i.product_id} data-testid={`audit-filter-item-${i.product_id}`}>
                    {i.product_id}{i.description ? ` · ${i.description.slice(0, 28)}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="h-9 flex-1" onClick={load} disabled={loading} data-testid="audit-refresh-btn">
              {loading ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
            </Button>
            <Button
              variant="outline" className="h-9 text-[#027A48] border-[#A6F4C5]"
              title="Download the full ledger as Excel" aria-label="Download Excel report"
              onClick={() => window.open(api.auditExportUrl("excel", filters), "_blank")}
              data-testid="audit-export-excel-btn"
            >
              <FileXls size={15} weight="bold" />
            </Button>
            <Button
              variant="outline" className="h-9 text-[#B42318] border-[#FECDCA]"
              title="Download the audit summary as PDF" aria-label="Download audit summary PDF"
              onClick={() => window.open(api.auditExportUrl("pdf", filters), "_blank")}
              data-testid="audit-export-pdf-btn"
            >
              <FilePdf size={15} weight="bold" />
            </Button>
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="py-20 text-center" data-testid="audit-loading">
          <CircleNotch size={26} weight="bold" className="animate-spin text-[#0E7C86] mx-auto" />
        </div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi
              label="Consumption value" testId="audit-kpi-value"
              value={`₹${money(summary.consumption_value)}`}
              sub={summary.returned_value
                ? `net ₹${money(summary.net_value)} after ₹${money(summary.returned_value)} returned · ${num(summary.posted_qty)} qty`
                : `${num(summary.posted_qty)} qty · ${summary.items} item(s) · ${summary.days} day(s)`}
            />
            <Kpi
              label="Today / last 7 days" testId="audit-kpi-day" tone="dark"
              value={`₹${money(periods.day.value)}`}
              sub={`7 days ₹${money(periods.week.value)}`}
            />
            <Kpi
              label="This month" testId="audit-kpi-month" tone="dark"
              value={`₹${money(periods.month.value)}`}
              sub={`${periods.month.from} onwards`}
            />
            <Kpi
              label="Financial year to date" testId="audit-kpi-fy" tone="dark"
              value={`₹${money(periods.fy.value)}`}
              sub={`since ${periods.fy.from}`}
            />
          </div>

          <p className="text-[11px] text-[#667085]" data-testid="audit-value-source-note">
            Every amount is SAP&apos;s own, read from the real-time journal report against the confirmation id that posted
            it - the Material Rate is SAP&apos;s value divided by its quantity, never a master price. Period figures are net
            of returns.
          </p>

          {(summary.flagged_rows > 0 || summary.awaiting_value_rows > 0 || summary.not_posted_rows > 0) && (
            <div className="border border-[#FEDF89] bg-[#FFFCF5] rounded-xl px-4 py-3 flex items-start gap-2" data-testid="audit-attention-banner">
              <WarningCircle size={16} weight="fill" className="text-[#B54708] mt-0.5 shrink-0" />
              <p className="text-[12px] text-[#B54708]">
                {summary.flagged_rows > 0 && (
                  <span data-testid="audit-flagged-note">
                    <strong>{summary.flagged_rows}</strong> day/item line(s) where SAP&apos;s posted quantity differs from
                    what the register declared.{" "}
                  </span>
                )}
                {summary.awaiting_value_rows > 0 && (
                  <span>
                    <strong>{summary.awaiting_value_rows}</strong> posted line(s) SAP has not valued yet - their quantity
                    counts, their money does not.{" "}
                  </span>
                )}
                {summary.not_posted_rows > 0 && (
                  <span>
                    <strong>{summary.not_posted_rows}</strong> declared line(s) were never posted to SAP.
                  </span>
                )}
              </p>
            </div>
          )}

          <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="audit-daily-panel">
            <h3 className="font-heading text-sm font-bold text-[#344054] mb-3 flex items-center gap-1.5">
              <TrendUp size={15} weight="bold" className="text-[#0E7C86]" /> Day by day
            </h3>
            <DayBars daily={data.daily} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="audit-top-departments">
              <h3 className="font-heading text-sm font-bold text-[#344054] mb-2 flex items-center gap-1.5">
                <Factory size={15} weight="bold" className="text-[#0E7C86]" /> Top departments by cost
              </h3>
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">Department</TableHead>
                    <TableHead className="text-xs">Site</TableHead>
                    <TableHead className="text-xs text-right">Net qty</TableHead>
                    <TableHead className="text-xs text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_departments.map((r) => (
                    <TableRow key={r.department_id} data-testid={`audit-top-department-${r.department}`}>
                      <TableCell className="text-xs font-bold text-[#101828]">{r.department}</TableCell>
                      <TableCell className="text-xs text-[#667085]">{r.site_id}</TableCell>
                      <TableCell className="text-xs text-right">{num(r.quantity)}</TableCell>
                      <TableCell className="text-xs text-right font-bold">₹{money(r.value)}</TableCell>
                    </TableRow>
                  ))}
                  {!data.top_departments.length && (
                    <TableRow><TableCell colSpan={4} className="text-xs text-[#667085]">Nothing in this period.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="audit-top-sites">
              <h3 className="font-heading text-sm font-bold text-[#344054] mb-2 flex items-center gap-1.5">
                <Factory size={15} weight="bold" className="text-[#0E7C86]" /> Top sites by cost
              </h3>
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">Site</TableHead>
                    <TableHead className="text-xs text-right">Items</TableHead>
                    <TableHead className="text-xs text-right">Net qty</TableHead>
                    <TableHead className="text-xs text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_sites.map((r) => (
                    <TableRow key={r.site_id} data-testid={`audit-top-site-${r.site_id}`}>
                      <TableCell className="text-xs font-bold text-[#101828]">{r.site_id}</TableCell>
                      <TableCell className="text-xs text-right text-[#667085]">{r.item_count}</TableCell>
                      <TableCell className="text-xs text-right">{num(r.quantity)}</TableCell>
                      <TableCell className="text-xs text-right font-bold">₹{money(r.value)}</TableCell>
                    </TableRow>
                  ))}
                  {!data.top_sites.length && (
                    <TableRow><TableCell colSpan={4} className="text-xs text-[#667085]">Nothing in this period.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="audit-top-items">
            <h3 className="font-heading text-sm font-bold text-[#344054] mb-2 flex items-center gap-1.5">
              <Package size={15} weight="bold" className="text-[#0E7C86]" /> Top 20 consumed items
            </h3>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F9FAFB]">
                    <TableHead className="text-xs">Item</TableHead>
                    <TableHead className="text-xs">Description</TableHead>
                    <TableHead className="text-xs text-right">Net qty</TableHead>
                    <TableHead className="text-xs">UOM</TableHead>
                    <TableHead className="text-xs text-right">Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.top_items.map((r) => (
                    <TableRow key={r.product_id} data-testid={`audit-top-item-${r.product_id}`}>
                      <TableCell className="text-xs font-mono font-bold text-[#101828]">{r.product_id}</TableCell>
                      <TableCell className="text-xs text-[#667085]">{r.description || "—"}</TableCell>
                      <TableCell className="text-xs text-right">{num(r.quantity)}</TableCell>
                      <TableCell className="text-xs">{r.uom}</TableCell>
                      <TableCell className="text-xs text-right font-bold">₹{money(r.value)}</TableCell>
                    </TableRow>
                  ))}
                  {!data.top_items.length && (
                    <TableRow><TableCell colSpan={5} className="text-xs text-[#667085]">Nothing in this period.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="bg-white border border-[#E4E7EC] rounded-xl p-4" data-testid="audit-ledger-panel">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h3 className="font-heading text-sm font-bold text-[#344054]">
                {view === "ledger" ? "Consumption ledger" : view === "declaration" ? "Declared vs posted to SAP" : "Weekly physical count"}
                <span className="font-normal text-[#667085] text-xs">
                  {view === "ledger" ? ` · ${data.rows.length} of ${data.row_total} line(s)` : ""}
                  {view === "declaration" ? ` · ${data.declaration_variance.length} mismatch(es)` : ""}
                  {view === "count" ? ` · ${data.count_variance.length} counted item(s)` : ""}
                </span>
              </h3>
              <div className="flex gap-1">
                {[["ledger", "Ledger"], ["declaration", "Declared vs SAP"], ["count", "Physical count"]].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setView(key)}
                    className={`text-xs font-bold px-2.5 py-1.5 rounded-md border transition-colors duration-150 ${
                      view === key ? "bg-[#0E7C86] text-white border-[#0E7C86]" : "bg-white text-[#475467] border-[#D0D5DD] hover:bg-[#F9FAFB]"
                    }`}
                    data-testid={`audit-view-${key}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="overflow-x-auto">
              {view === "ledger" && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Site</TableHead>
                      <TableHead className="text-xs">Department</TableHead>
                      <TableHead className="text-xs">Item</TableHead>
                      <TableHead className="text-xs">Description</TableHead>
                      <TableHead className="text-xs">UOM</TableHead>
                      <TableHead className="text-xs text-right">Declared</TableHead>
                      <TableHead className="text-xs text-right">Consumed</TableHead>
                      <TableHead className="text-xs text-right">Returned</TableHead>
                      <TableHead className="text-xs text-right">Rate</TableHead>
                      <TableHead className="text-xs text-right">Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.rows.map((r) => (
                      <TableRow
                        key={`${r.date}-${r.department_id}-${r.product_id}`}
                        className={r.flagged ? "bg-[#FFFCF5]" : ""}
                        data-testid={`audit-row-${r.date}-${r.product_id}`}
                      >
                        <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                        <TableCell className="text-xs">{r.site_id}</TableCell>
                        <TableCell className="text-xs">{r.department}</TableCell>
                        <TableCell className="text-xs font-mono font-bold text-[#101828]">{r.product_id}</TableCell>
                        <TableCell className="text-xs text-[#667085] max-w-[220px] truncate">{r.description || "—"}</TableCell>
                        <TableCell className="text-xs">{r.uom}</TableCell>
                        <TableCell className="text-xs text-right">{num(r.declared_qty)}</TableCell>
                        <TableCell className={`text-xs text-right ${r.flagged ? "font-bold text-[#B54708]" : ""}`}>
                          {num(r.posted_qty)}
                        </TableCell>
                        <TableCell className="text-xs text-right">{r.returned_qty ? num(r.returned_qty) : "—"}</TableCell>
                        <TableCell className="text-xs text-right">{r.rate === null ? "—" : money(r.rate)}</TableCell>
                        <TableCell className="text-xs text-right">
                          {r.net_value === null ? (
                            <span className={VALUE_NOTE[r.value_status].className}>{VALUE_NOTE[r.value_status].label}</span>
                          ) : (
                            <span className="font-bold">₹{money(r.net_value)}</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!data.rows.length && (
                      <TableRow><TableCell colSpan={11} className="text-sm text-[#667085]" data-testid="audit-ledger-empty">
                        No consumption recorded in this period.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              {view === "declaration" && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead className="text-xs">Date</TableHead>
                      <TableHead className="text-xs">Site</TableHead>
                      <TableHead className="text-xs">Department</TableHead>
                      <TableHead className="text-xs">Item</TableHead>
                      <TableHead className="text-xs text-right">Declared</TableHead>
                      <TableHead className="text-xs text-right">Posted to SAP</TableHead>
                      <TableHead className="text-xs text-right">Difference</TableHead>
                      <TableHead className="text-xs">What it means</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.declaration_variance.map((r) => (
                      <TableRow key={`${r.date}-${r.department_id}-${r.product_id}`} data-testid={`audit-variance-${r.date}-${r.product_id}`}>
                        <TableCell className="text-xs whitespace-nowrap">{r.date}</TableCell>
                        <TableCell className="text-xs">{r.site_id}</TableCell>
                        <TableCell className="text-xs">{r.department}</TableCell>
                        <TableCell className="text-xs font-mono font-bold">{r.product_id}</TableCell>
                        <TableCell className="text-xs text-right">{num(r.declared_qty)}</TableCell>
                        <TableCell className="text-xs text-right">{num(r.net_qty)}</TableCell>
                        <TableCell className={`text-xs text-right font-bold ${r.variance_qty > 0 ? "text-[#B42318]" : "text-[#B54708]"}`}>
                          {r.variance_qty > 0 ? "+" : ""}{num(r.variance_qty)}
                        </TableCell>
                        <TableCell className="text-[11px] text-[#667085]">
                          {r.value_status === "not_posted"
                            ? "Declared on the floor, never posted to SAP"
                            : r.variance_qty > 0
                              ? "SAP was charged more than the register declared"
                              : "SAP was charged less than the register declared"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!data.declaration_variance.length && (
                      <TableRow><TableCell colSpan={8} className="text-sm text-[#027A48]" data-testid="audit-variance-empty">
                        Every posted quantity matches the register declaration in this period.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}

              {view === "count" && (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead className="text-xs">Week</TableHead>
                      <TableHead className="text-xs">Site</TableHead>
                      <TableHead className="text-xs">Department</TableHead>
                      <TableHead className="text-xs">Item</TableHead>
                      <TableHead className="text-xs text-right">Declared</TableHead>
                      <TableHead className="text-xs text-right">Counted</TableHead>
                      <TableHead className="text-xs text-right">Actual used</TableHead>
                      <TableHead className="text-xs text-right">Variance</TableHead>
                      <TableHead className="text-xs">Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.count_variance.map((r) => (
                      <TableRow
                        key={`${r.department_id}-${r.week_no}-${r.product_id}`}
                        className={r.beyond_tolerance ? "bg-[#FFFCF5]" : ""}
                        data-testid={`audit-count-${r.department_id}-${r.week_no}-${r.product_id}`}
                      >
                        <TableCell className="text-xs whitespace-nowrap">#{r.week_no} · {r.end_date}</TableCell>
                        <TableCell className="text-xs">{r.site_id}</TableCell>
                        <TableCell className="text-xs">{r.department}</TableCell>
                        <TableCell className="text-xs font-mono font-bold">{r.product_id}</TableCell>
                        <TableCell className="text-xs text-right">{num(r.declared)}</TableCell>
                        <TableCell className="text-xs text-right">{num(r.counted)}</TableCell>
                        <TableCell className="text-xs text-right">{num(r.actual)}</TableCell>
                        <TableCell className={`text-xs text-right font-bold ${r.beyond_tolerance ? "text-[#B42318]" : "text-[#475467]"}`}>
                          {num(r.variance)} {r.variance_pct ? `(${num(r.variance_pct, 1)}%)` : ""}
                        </TableCell>
                        <TableCell className="text-[11px] text-[#667085] max-w-[200px] truncate">{r.reason || "—"}</TableCell>
                      </TableRow>
                    ))}
                    {!data.count_variance.length && (
                      <TableRow><TableCell colSpan={9} className="text-sm text-[#667085]" data-testid="audit-count-empty">
                        No week was closed with a physical count in this period.
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
